import { useContext } from "react"
import { BlockContext } from "../context/block-context"
import { mergeValues } from "../blocks/text/utils"
import { getBlockByType } from "../blocks/utils"
import { useActiveBlock } from "../hooks/use-active-block"
import { uuid } from "utils/uuid"
import {
  getChildBlocks,
  isDirectChild,
  getSiblingsForBlock
} from "../utils/block-traversal"

export function useBlocks(block_id) {
  const { blocks, dispatch, updateProperties } = useContext(BlockContext) || {}
  const { activeBlock, setActiveBlockId } = useActiveBlock()

  const block = blocks && blocks.find(block => block.block_id === block_id)

  const properties = block?.properties

  const children = blocks && getChildBlocks(blocks, block_id)

  /**
   * Add a block, or array of blocks, as children on the current element.
   * Returns the block_ids of the added blocks.
   *
   * Optionally accepts an `options` object as a second parameter. `options` can
   * contain values for `position` (the index to place the blocks) and `focus`
   * (whether to focus the block after adding it).
   *
   * @param {Object|Array} blocks
   * @param {Object} [options]
   * @param {Integer} [options.position = end]
   * @param {Boolean} [options.focus = true]
   */

  function add(blocks, options = {}) {
    const { position, focus = true } = options

    const newBlocks = assembleNewBlocks(blocks, block_id)
    const positionedBlocks = addAtPosition(newBlocks, children, position)
    updateWithOrders(dispatch, positionedBlocks)
    focus && setActiveBlockId(newBlocks[0].block_id)
    return newBlocks.map(block => block.block_id)
  }

  /**
   * Removes a block matching the `block_id`.
   *
   * @param {String} block_id - the block_id of the block to delete
   */

  function remove(block_id) {
    // Merge adjacent text blocks. We filter out the block that's about to be
    // removed, merge adjacent text areas in the resulting array, and dispatch
    // updates to each block.
    const siblings = getSiblingsForBlock(block_id, blocks)
    const remainingBlocks = siblings.filter(b => b.block_id !== block_id)
    const mergedBlocks = mergeAdjacentTextBlocks(remainingBlocks)

    dispatch({ block_id: block_id, _destroy: true })
    updateWithOrders(dispatch, mergedBlocks)

    // Recursively delete all children of this block.
    blocks.map(
      block => isDirectChild(block, block_id) && remove(block.block_id)
    )

    // If the removed block was open in the sidebar, stop showing it.
    if (activeBlock?.block_id === block_id) setActiveBlockId(null)
  }

  /**
   * Updates the current block with the data passed in.
   *
   * @param {Object} data - object containing the data you'd like to update
   */

  function update(data, options = {}) {
    if (options.localStoragePrefix) {
      saveToLocalStorage(data, options.localStoragePrefix)
    }

    updateProperties(block_id, data)
  }

  /**
   * Reorder a block from position `from` to position `to`.
   *
   * @param {Integer} from
   * @param {Integer} to
   */

  function reorder(from, to) {
    const reorderedBlocks = [...children]
    const [removed] = reorderedBlocks.splice(from, 1)
    reorderedBlocks.splice(to, 0, removed)
    const mergedBlocks = mergeAdjacentTextBlocks(reorderedBlocks)
    updateWithOrders(dispatch, mergedBlocks)
  }

  /**
   * Replaces the full properties object for a block with a new object.
   *
   * @param {Object} properties
   */

  function replaceProperties(properties) {
    dispatch({ block_id, properties })
  }

  return {
    block,
    properties,
    children,
    add,
    remove,
    update,
    reorder,
    replaceProperties
  }
}

/**
 * Assembles an array of blocks and assigns their `block_id`s and `parent_id`s.
 * If necessary, you can explicitly pass these values to overwrite them.
 *
 * @param {Array} newBlocks - a block object, or an array of block objects
 * @param {String} parent_id - the parent's block_id
 */

export function assembleNewBlocks(newBlocks, parent_id) {
  return [].concat(newBlocks).map(block => {
    const { defaults } = getBlockByType(block.type)
    return {
      block_id: uuid(),
      parent_id,
      properties: { ...defaults },
      ...block
    }
  })
}

/**
 * Dispatches an update to adjust the order for each block in an array.
 *
 * @param {Function} dispatch - reducer's dispatch function
 * @param {Array} newBlocks - array of block objects
 */

export function updateWithOrders(dispatch, blocks) {
  return blocks.map((block, index) => {
    const newBlock = { ...block, order: index }
    dispatch(newBlock)
    return newBlock
  })
}

/**
 * Returns an array of blocks where adjacent text blocks have been merged.
 *
 * Pass in an ordered array of non-deleted blocks that belong to a parent.
 * This function will look for adjacent blocks of type "text", and if they
 * exist, combine their values. The first text block will hold the merged
 * values, and the second block will be marked for destruction. This is
 * purely functional and does not dispatch an update.
 *
 * @param {Array} blocks
 */

export function mergeAdjacentTextBlocks(blocks) {
  const firstAdjacentTextBlock = blocks.findIndex((current, index) => {
    const next = blocks[index + 1]
    if (!current || !next) return false
    return current.type === "text" && next.type === "text"
  })

  if (firstAdjacentTextBlock === -1) return blocks

  return blocks.map((block, index) => {
    const isBlockToMerge = index === firstAdjacentTextBlock
    const isBlockToDelete = index === firstAdjacentTextBlock + 1
    const next = blocks[index + 1]

    if (isBlockToMerge) {
      const firstValue = block.properties.value
      const secondValue = next.properties.value
      const mergedValue = mergeValues(firstValue, secondValue)
      const properties = { ...block.properties, value: mergedValue }
      return { ...block, properties }
    } else if (isBlockToDelete) {
      return { ...block, _destroy: true }
    }

    return block
  })
}

/*
 * Adds items to an array at a specific index. If `index` is omitted, they'll
 * be added to the end of the array. Returns the completed array.
 *
 * @param {Array|Object} items
 * @param {Array} array
 * @param {Integer} index
 */

export function addAtPosition(items, array, index) {
  index = index ?? array.length
  const result = [...array]
  result.splice(index, 0, ...items)
  return result
}

/**
 * saveToLocalStorage saves each key-value pair inside `data` to local storage.
 * The stored item is given a key containing the supplied `prefix` and the key-
 * value pair's key. For example:
 *
 * ```js
 * saveToLocalStorage({ name: "Maya", color: "calico" }, "cat")
 *
 * window.localStorage.getItem("cat-name")  // "Maya"
 * window.localStorage.getItem("cat-color") // "calico"
 * ```
 *
 * @param {Object} data
 * @param {String} prefix
 */

export function saveToLocalStorage(data, prefix) {
  for (const key in data) {
    const name = `${prefix}-${key}`
    window.localStorage.setItem(name, data[key])
  }
}
